import { useCallback, useMemo } from 'react';
import { useApiClient } from 'hooks/useApiClient';
import { API_ROUTES } from 'utils/constants';
import {
  Product,
  CreateProductRequest,
  UpdateProductRequest,
} from 'types/product';

/**
 * Product API service
 * Consolidates all product-related API calls in a single location
 */
export function useProductService() {
  const apiClient = useApiClient();

  /**
   * Get all products
   * @param includeVoided - Include voided products (default: false)
   */
  const getAllProducts = useCallback(
    async (includeVoided: boolean = false): Promise<Product[]> => {
      const queryParams: Record<string, string> = {};
      if (includeVoided) {
        queryParams.includeVoided = 'true';
      }
      return apiClient.get<Product[]>(API_ROUTES.PRODUCT, queryParams);
    },
    [apiClient],
  );

  /**
   * Get a single product by ID
   */
  const getProductById = useCallback(
    async (id: string): Promise<Product> => {
      return apiClient.get<Product>(`${API_ROUTES.PRODUCT}/${id}`);
    },
    [apiClient],
  );

  /**
   * Create a new product
   * Note: POST /products now requires companyId as query parameter
   */
  const createProduct = useCallback(
    async (product: CreateProductRequest): Promise<Product> => {
      // companyId is now passed as query parameter automatically by apiClient.post()
      return apiClient.post<Product>(
        API_ROUTES.PRODUCT,
        product as unknown as Record<string, unknown>,
      );
    },
    [apiClient],
  );

  /**
   * Update an existing product
   */
  const updateProduct = useCallback(
    async (id: string, product: UpdateProductRequest): Promise<Product> => {
      return apiClient.patch<Product>(
        `${API_ROUTES.PRODUCT}/${id}`,
        product as unknown as Record<string, unknown>,
      );
    },
    [apiClient],
  );

  /**
   * Delete (void) a product
   * @param id - Product ID
   * @param reason - Optional reason for voiding
   */
  const deleteProduct = useCallback(
    async (id: string, reason?: string): Promise<void> => {
      const queryParams: Record<string, string> = {};
      if (reason) {
        queryParams.reason = reason;
      }
      await apiClient.delete(`${API_ROUTES.PRODUCT}/${id}`, queryParams);
    },
    [apiClient],
  );

  /**
   * Get product history
   */
  const getProductHistory = useCallback(
    async (id: string): Promise<Product[]> => {
      return apiClient.get<Product[]>(`${API_ROUTES.PRODUCT}/${id}/history`);
    },
    [apiClient],
  );

  /**
   * Rollback product to a previous version
   */
  const rollbackProduct = useCallback(
    async (id: string, snapshotId: string): Promise<Product> => {
      return apiClient.patch<Product>(
        `${API_ROUTES.PRODUCT}/${id}/rollback/${snapshotId}`,
      );
    },
    [apiClient],
  );

  /**
   * Save a product as draft
   */
  const saveDraftProduct = useCallback(
    async (product: CreateProductRequest): Promise<Product> => {
      return apiClient.post<Product>(
        API_ROUTES.PRODUCT_DRAFT,
        product as unknown as Record<string, unknown>,
      );
    },
    [apiClient],
  );

  /**
   * Get all draft products
   */
  const getAllDraftProducts = useCallback(async (): Promise<Product[]> => {
    return apiClient.get<Product[]>(API_ROUTES.PRODUCT_DRAFT);
  }, [apiClient]);

  /**
   * Update a draft product
   */
  const updateDraftProduct = useCallback(
    async (id: string, product: UpdateProductRequest): Promise<Product> => {
      return apiClient.patch<Product>(
        `${API_ROUTES.PRODUCT_DRAFT}/${id}`,
        product as unknown as Record<string, unknown>,
      );
    },
    [apiClient],
  );

  /**
   * Publish a draft product
   */
  const publishDraftProduct = useCallback(
    async (id: string): Promise<Product> => {
      return apiClient.post<Product>(
        `${API_ROUTES.PRODUCT_DRAFT}/${id}/publish`,
      );
    },
    [apiClient],
  );

  /**
   * Restore (unvoid) a product
   * Note: Uses POST method per API documentation
   */
  const restoreProduct = useCallback(
    async (id: string): Promise<Product> => {
      return apiClient.post<Product>(`${API_ROUTES.PRODUCT}/${id}/restore`);
    },
    [apiClient],
  );

  return useMemo(
    () => ({
      getAllProducts,
      getProductById,
      createProduct,
      updateProduct,
      deleteProduct,
      getProductHistory,
      rollbackProduct,
      saveDraftProduct,
      getAllDraftProducts,
      updateDraftProduct,
      publishDraftProduct,
      restoreProduct,
    }),
    [
      getAllProducts,
      getProductById,
      createProduct,
      updateProduct,
      deleteProduct,
      getProductHistory,
      rollbackProduct,
      saveDraftProduct,
      getAllDraftProducts,
      updateDraftProduct,
      publishDraftProduct,
      restoreProduct,
    ],
  );
}
